/**
 * Star surface shader.
 *
 * Built to be reused in flight, not only on the map — nothing here depends on
 * the starmap's scale or camera. Two layers of counter-drifting warped noise
 * give convection granulation, with darker sunspots where the field is low and
 * brighter flare cells where it peaks.
 *
 * Logarithmic depth chunks are included by hand. Three only injects those into
 * its own materials, and the map runs `logarithmicDepthBuffer: true`; a custom
 * shader without them writes ordinary depth and z-fights against everything.
 */

import * as THREE from 'three';

import { NOISE_GLSL } from './noise.glsl';

const vertexShader = /* glsl */ `
  // <common> must come first: the log-depth chunks call isPerspectiveMatrix,
  // which is defined there. Without it the program fails to link and the object
  // silently draws nothing at all — no error, just an invisible star.
  #include <common>
  #include <logdepthbuf_pars_vertex>

  varying vec3 vLocal;
  varying vec3 vNormalW;
  varying vec3 vViewDir;

  void main() {
    vLocal = position;
    vNormalW = normalize(mat3(modelMatrix) * normal);

    vec4 world = modelMatrix * vec4(position, 1.0);
    vViewDir = normalize(cameraPosition - world.xyz);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    #include <logdepthbuf_vertex>
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;
  #include <common>
  #include <logdepthbuf_pars_fragment>

  ${NOISE_GLSL}

  varying vec3 vLocal;
  varying vec3 vNormalW;
  varying vec3 vViewDir;

  uniform vec3 uColor;
  uniform float uTime;
  uniform float uSeed;
  uniform int uOctaves;

  void main() {
    vec3 dir = normalize(vLocal);
    vec3 p = dir * 2.6 + uSeed;

    // Two layers drifting against each other, so the surface churns rather
    // than sliding in one direction.
    float slow = warped(p + vec3(0.0, 0.0, uTime * 0.03), uOctaves, 1.4);
    float fast = warped(p * 2.7 - vec3(0.0, uTime * 0.05, 0.0), uOctaves, 0.9);
    float field = slow * 0.65 + fast * 0.35;

    // Granulation, then spots where the field sinks and flares where it peaks.
    float granules = smoothstep(0.35, 0.72, field);
    float spots = smoothstep(0.34, 0.16, field);
    float flares = smoothstep(0.66, 0.88, field);

    // Hotter cores read as washed-out toward white; cooler troughs push toward
    // the colour's own deep end rather than toward black, which would look
    // like dirt on a lamp.
    vec3 deep = uColor * 0.35;
    vec3 mid = uColor;
    vec3 hot = mix(uColor, vec3(1.0), 0.72);

    vec3 color = mix(deep, mid, granules);
    color = mix(color, hot, flares);
    color = mix(color, deep * 0.55, spots * 0.8);

    // Limb darkening, then a rim brightening on top: the edge of a star is
    // dimmer than its centre, but the chromosphere just past it glows.
    float facing = max(dot(normalize(vNormalW), normalize(vViewDir)), 0.0);
    color *= 0.55 + 0.45 * pow(facing, 0.55);
    color += uColor * pow(1.0 - facing, 3.0) * 0.9;

    gl_FragColor = vec4(color, 1.0);
    #include <logdepthbuf_fragment>
  }
`;

export interface SunMaterialOptions {
  color: THREE.ColorRepresentation;
  seed: number;
  /** Map view uses fewer octaves; flight can afford more. */
  quality?: 'low' | 'high';
}

export function createSunMaterial(options: SunMaterialOptions): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uColor: { value: new THREE.Color(options.color) },
      uTime: { value: 0 },
      uSeed: { value: (options.seed % 1000) * 0.017 },
      uOctaves: { value: options.quality === 'high' ? 6 : 4 },
    },
  });
}

/**
 * The glow around a star.
 *
 * A camera-facing sprite would be simpler, but it breaks the moment anything
 * passes in front of the star. This is a back-faced shell whose alpha falls off
 * toward its own silhouette, so it behaves like volume.
 */
export function createSunGlowMaterial(color: THREE.ColorRepresentation): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    uniforms: { uColor: { value: new THREE.Color(color) } },
    vertexShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      varying vec3 vNormalW;
      varying vec3 vViewDir;
      void main() {
        vNormalW = normalize(mat3(modelMatrix) * normal);
        vec4 world = modelMatrix * vec4(position, 1.0);
        vViewDir = normalize(cameraPosition - world.xyz);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        #include <logdepthbuf_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      #include <common>
      #include <logdepthbuf_pars_fragment>
      varying vec3 vNormalW;
      varying vec3 vViewDir;
      uniform vec3 uColor;
      void main() {
        // Brightest where the shell passes closest to the star and fading to
        // nothing at its own silhouette.
        //
        // This was inverted before: (1.0 - facing) peaks *at* the silhouette,
        // which drew a bright ring at the shell's edge and made every star look
        // like it was inside a glass bauble. The star's own depth hides the
        // middle, so what remains is the annulus around it: a corona.
        float facing = abs(dot(normalize(vNormalW), normalize(vViewDir)));
        float halo = pow(facing, 3.0);
        gl_FragColor = vec4(uColor, halo * 0.45);
        #include <logdepthbuf_fragment>
      }
    `,
  });
}
